import {
  Box,
  Spinner,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  CheckAvailabilityDocument,
  CompensationsPayrollConceptsDocument,
  PayrollValueObjectsConceptCategoryEnum,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'

import { SalaryTypesByLegalEntity } from 'modules/payroll_integrations_datev_lauds/components/SettingsSteps/Steps/SalaryTypesStep/SalaryTypesByLegalEntity'
import { getMatchedLegalEntities } from 'modules/payroll_integrations_datev_lauds/components/SettingsSteps/Steps/helper'
import {
  DatevMapping,
  DatevSource,
  DatevWizardData,
} from 'modules/payroll_integrations_datev_lauds/lib/types'

const datevCompensationSchema = z.object({
  mappings: z
    .array(
      z.object({
        factorial_id: z.string(),
        datev_id: z.string(),
        meta: z.object({
          legal_entity_id: z.string(),
        }),
      })
    )
    .optional(),
})

const SalaryTypesStep = ({ data, onDataChanged }: WizardStepRenderParams<DatevWizardData>) => {
  const {
    access: { id: accessId },
    company,
  } = useCurrentSession()
  const { data: featuresData } = useQuery(CheckAvailabilityDocument, {
    variables: { companyId: company.id },
  })

  const moduleAvailabilities = featuresData?.companies?.moduleAvailabilitiesConnection?.nodes || []

  const compensationsEnabled = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.compensations
  )

  const trackEvent = () => {
    track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsSettingsSalaryTypessMapped, {
      company_id: company.id,
      access_id: accessId,
      mapped_compensations: data?.sources?.compensations?.mappings?.length ?? 0,
    })
  }
  const compensations: DatevSource = data.sources?.compensations || {}

  useUnsafeEffect(() => {
    if (data.sources?.compensations === undefined && data.answers.compensations !== undefined) {
      const parsedData = datevCompensationSchema.safeParse(data.answers.compensations)
      onDataChanged({
        ...data,
        sources: {
          ...data.sources,
          compensations: parsedData.success ? parsedData.data : {},
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useOnNext(() => {
    trackEvent()
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        compensations: JSON.parse(JSON.stringify(compensations)),
      },
    })
  })

  const updateMappings = (newMappings: DatevMapping[]) => {
    onDataChanged({
      ...data,
      sources: {
        ...data.sources,
        compensations: { mappings: newMappings },
      },
    })
  }

  const payrollConceptCategories: PayrollValueObjectsConceptCategoryEnum[] = [
    'earnings_fixed_salary',
    'earnings_variable',
    'earnings_benefits_in_kind',
    'earnings_others',
    'deductions',
  ]

  const { data: conceptsData, loading: conceptsLoading } = useQuery(
    CompensationsPayrollConceptsDocument,
    {
      variables: {
        companyId: company.id,
        categories: payrollConceptCategories,
        withActiveStatus: true,
      },
    }
  )

  const payrollConcepts = conceptsData?.payroll.concepts || []

  const sortedActivePayrollConcepts = [...payrollConcepts].sort((a, b) =>
    a.label.localeCompare(b.label)
  )

  const matchedLegalEntities = getMatchedLegalEntities(data, company)

  if (conceptsLoading) {
    return <Spinner />
  }
  return (
    <Box flexDirection="row" gap="s96">
      <Box gap="s24">
        <Title
          title={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.wage_type.title'
          )}
          description={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.wage_type.description'
          )}
        />
        <Box justifyContent="flexStart" flexDirection="column" gap="s32">
          {matchedLegalEntities.map((legalEntity) => {
            return (
              <SalaryTypesByLegalEntity
                data={data}
                key={`legal_entity_${legalEntity.id}`}
                legalEntity={legalEntity}
                payrollConcepts={compensationsEnabled ? sortedActivePayrollConcepts : []}
                onMappingChanged={updateMappings}
              />
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export const salaryTypesStep = (): WizardStep<DatevWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.wage_type.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <SalaryTypesStep {...props} />,
  isCompleted: (_data) => true,
})
